import React, { useState } from "react";
import { Loader2, Sparkles, Check, X, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useJob, JobProgress } from "./WorkbenchJobs";

const KIND = {
  ok: "text-[#2F6B33] bg-[#E7F1E4] border-[#BCD5B5]",
  blocked: "text-[#8A5A00] bg-[#FBF1DD] border-[#E4D3A8]",
  fail: "text-[#8A1C1C] bg-[#F7E3E1] border-[#E2B7B2]",
};

const SourceLine = ({ s }) => (
  <li className="flex items-center gap-2 text-xs">
    <span className={`font-mono-cv text-[10px] px-1.5 py-0.5 rounded-sm border ${KIND[s.kind] || KIND.fail}`}>{s.kind === "ok" ? "verified" : s.kind === "blocked" ? "bot-blocked" : "broken"} {s.status || ""}</span>
    <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-[#9E4535] hover:underline truncate inline-flex items-center gap-1">
      {s.label} <ExternalLink className="w-3 h-3 shrink-0" />
    </a>
  </li>
);

const ProposalRow = ({ r, onDecided }) => {
  const [busy, setBusy] = useState(false);
  const p = r.source_proposal;
  const decide = async (accept) => {
    setBusy(true);
    try {
      const res = await api.post("/admin/curios/workbench/legacy/decide", { slug: r.slug, accept });
      toast.success(accept ? `Upgraded: ${res.data.source_status === "verified" ? "fact-checked" : "unverified"}` : "Proposal rejected");
      onDecided(accept);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); setBusy(false); }
  };
  return (
    <div className="border-t border-[#E9E1D0] py-3" data-testid={`legacy-proposal-${r.slug}`}>
      <p className="font-serif-display text-base font-semibold text-[#1A1615]">{r.title}</p>
      <ul className="mt-1 space-y-1">{p.sources.map((s) => <SourceLine key={s.url} s={s} />)}</ul>
      {p.sources.length === 0 && <p className="text-xs text-[#8A1C1C] mt-1">No verifiable source found. Re-propose or edit manually.</p>}
      <div className="flex gap-2 mt-2">
        <button data-testid={`legacy-approve-${r.slug}`} disabled={busy || !p.sources.length} onClick={() => decide(true)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#FAF7F0] bg-[#2F6B33] px-2.5 py-1 rounded-sm disabled:opacity-50"><Check className="w-3.5 h-3.5" /> Approve</button>
        <button data-testid={`legacy-reject-${r.slug}`} disabled={busy} onClick={() => decide(false)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#8A1C1C] border border-[#E2B7B2] px-2.5 py-1 rounded-sm disabled:opacity-50"><X className="w-3.5 h-3.5" /> Reject</button>
      </div>
    </div>
  );
};

export const LegacyUpgradePanel = ({ rows, onChange }) => {
  const proposed = rows.filter((r) => r.source_proposal);
  const unproposed = rows.length - proposed.length;
  const { job, start, running } = useJob((j) => { toast.success(`Proposals ready for ${j.done - j.failed} curios`); onChange(); });
  return (
    <div className="bg-white border border-[#E6C2A0] rounded-sm p-4 mb-4" data-testid="legacy-upgrade-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-serif-display text-lg font-bold text-[#1A1615]">Legacy Source Upgrade</p>
          <p className="font-ui text-xs text-[#7A6C5D]">Gemini suggests a Wikipedia page plus a reputable secondary; every link is checked. One verified link is enough to publish.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="legacy-propose-btn" disabled={running || !unproposed} onClick={() => start("/admin/curios/workbench/legacy/propose").catch(() => toast.error("Could not start"))}
            className="inline-flex items-center gap-1.5 bg-[#9E4535] text-[#FAF7F0] font-ui text-xs font-semibold px-3 py-2 rounded-sm hover:bg-[#82382B] disabled:opacity-50">
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Propose upgrades ({unproposed})
          </button>
          <JobProgress job={job} testid="legacy-propose-progress" />
        </div>
      </div>
      {proposed.length > 0 && (
        <div className="mt-3 max-h-[480px] overflow-y-auto pr-1" data-testid="legacy-proposals-list">
          <p className="font-mono-cv text-[10px] uppercase tracking-wider text-[#7A6C5D] mb-1">{proposed.length} awaiting your decision</p>
          {proposed.map((r) => <ProposalRow key={r.slug} r={r} onDecided={onChange} />)}
        </div>
      )}
    </div>
  );
};
